import { Sentry } from '@opendoor/observability';
import { isNaN, isNumber, isString } from 'lodash';

import { FlowContext } from 'src/lib/flow';
import { FlowPage } from 'src/lib/flow/FlowPage';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { home } from 'src/lib/flow/text';
import { checkValidationForActiveQuestions } from 'src/lib/flow/validation';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';

// used on the review page to see if the user should be sent directly to the basic-data page
export const hasOnlyValidBasicDataAnswers: FlowPage['isEnabled'] = (ctx) => {
  const validations = checkValidationForActiveQuestions(basicDataPage.questions(ctx), ctx.answers);
  return validations.invalid.length == 0 && validations.customValidatorErrorMessages.length == 0;
};

export const hideBasementFields = (answers: FlowContext['answers']) => {
  const dwellingType = answers['home.dwelling_type'];
  if (dwellingType === 'apartment_condo') {
    return true;
  }
  return false;
};

export function basementFinishedSqFtCustomValidator(answerSet: FlowContext['answers']) {
  const basement_finished_sq_ft = answerSet['home.basement_finished_sq_ft'];
  const basement_unfinished_sq_ft = answerSet['home.basement_unfinished_sq_ft'];
  const above_grade_sq_ft = answerSet['home.above_grade_sq_ft'];
  const has_basement = answerSet['home.has_basement'] === 'yes';

  if (basement_finished_sq_ft === 0 && has_basement) {
    if (basement_unfinished_sq_ft === 0 || !basement_unfinished_sq_ft) {
      return 'Either finished or unfinished basement sq. ft. is required';
    }
  }

  if (
    typeof basement_finished_sq_ft === 'number' &&
    basement_finished_sq_ft < 100 &&
    basement_finished_sq_ft > 0
  ) {
    return 'Finished basement must be between 100 - 10,000 sq. ft.';
  }

  if (!basement_finished_sq_ft || !above_grade_sq_ft) {
    return;
  }

  if (typeof basement_finished_sq_ft !== 'number' || typeof above_grade_sq_ft !== 'number') {
    Sentry.withScope((scope) => {
      scope.setExtras({
        basement_finished_sq_ft,
        above_grade_sq_ft,
      });
      Sentry.captureMessage('basementFinishedSqFtCustomValidator: invalid input type');
    });
    return;
  }

  if (basement_finished_sq_ft > 1.2 * above_grade_sq_ft) {
    return 'Finished basement sq. ft. must be less than 120% of above ground sq. ft.';
  }
}

export function basementUnfinishedSqFtCustomValidator(answerSet: FlowContext['answers']) {
  const basement_unfinished_sq_ft = answerSet['home.basement_unfinished_sq_ft'];
  const basement_finished_sq_ft = answerSet['home.basement_finished_sq_ft'];
  const above_grade_sq_ft = answerSet['home.above_grade_sq_ft'];
  const has_basement = answerSet['home.has_basement'] === 'yes';

  if (basement_unfinished_sq_ft === 0 && has_basement) {
    if (basement_finished_sq_ft === 0 || !basement_finished_sq_ft) {
      return 'Either finished or unfinished basement sq. ft. is required';
    }
  }

  if (
    typeof basement_unfinished_sq_ft === 'number' &&
    basement_unfinished_sq_ft < 100 &&
    basement_unfinished_sq_ft > 0
  ) {
    return 'Unfinished basement must be between 100 - 10,000 sq. ft.';
  }

  if (!basement_unfinished_sq_ft || !above_grade_sq_ft) {
    return;
  }

  if (typeof basement_unfinished_sq_ft !== 'number' || typeof above_grade_sq_ft !== 'number') {
    Sentry.withScope((scope) => {
      scope.setExtras({
        basement_unfinished_sq_ft,
        above_grade_sq_ft,
      });
      Sentry.captureMessage('basementUnfinishedSqFtCustomValidator: invalid input type');
    });
    return;
  }

  if (basement_unfinished_sq_ft > 1.2 * above_grade_sq_ft) {
    return 'Unfinished basement sq. ft. must be less than 120% of above ground sq. ft.';
  }
}

// Copied from CinderBlocks https://github.com/opendoor-labs/code/blob/master/js/packages/cinderblocks/src/buy/PurchaseCalculator/helpers/format.ts#L10
const numberWithCommas = (n: number | string) => {
  if ((!isNumber(n) || !!isNaN(n)) && !isString(n)) {
    return;
  }
  const parts = n.toString().split('.');
  return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (parts[1] ? `.${parts[1]}` : '');
};

export function numberInputWithinValidRangeCustomErrorMessage(
  questionKey: string,
  value: AnswerSet[string] | number,
  minValue: number,
  maxValue: number,
  label: string,
  unit: string,
) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    /**
     * Ignore null and undefined
     *  - undefined: when we have no prefills
     *  - null: user cleared their input
     */
    if (typeof value === 'undefined' || value === null) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setExtras({
        questionKey,
        value,
        minValue,
        maxValue,
        label,
        unit,
      });
      Sentry.captureMessage('numberInputWithinValidRangeCustomErrorMessage: invalid input type');
    });
    return;
  }

  if (value < minValue || value > maxValue) {
    return `${label} must be between ${numberWithCommas(minValue)} - ${numberWithCommas(
      maxValue,
    )} ${unit}`;
  }
}

export function lotSizeNeedsRemediation(
  answers: AnswerSet,
  answerPrefills: FlowContext['inputContext']['answerPrefills'],
) {
  const dwellingTypePrefill = answerPrefills['home.dwelling_type'];
  const isSingleFamily =
    dwellingTypePrefill?.value === 'single_family' ||
    answers['home.dwelling_type'] === 'single_family';
  const lotSqFtPrefill = answerPrefills['home.lot_sq_ft'];

  if (isSingleFamily && !lotSqFtPrefill) {
    return true;
  }
  if (dwellingTypePrefill && lotSqFtPrefill) {
    return isSingleFamily && (lotSqFtPrefill.value < 1000 || lotSqFtPrefill.value > 108900);
  }
  return false;
}

const basicDataPage: FlowPage = {
  slug: () => 'basic-data',
  title: (): string => {
    return `Do these ${home()} details look right to you?`;
  },
  isEnabled: (ctx) => {
    if (
      !ctx.inputContext.eligibleProducts.includes('sell_with_exclusives') &&
      hasOnlyValidBasicDataAnswers(ctx)
    ) {
      // review page is enabled, so see if user selected the edit button
      return !!ctx.answers['seller.edit_home_details'];
    }
    // review page is not enabled, so user needs to fill in details
    return true;
  },

  autoAdvance: () => false,
  subtitle: () => 'Your offer will be more accurate if all these details are up to date.',
  subtitleElement: (experimentFlags) => {
    if (experimentFlags.experimentFlags.addressEntryConfirmation === 'treatment') {
      return;
    }
    return {
      type: 'element',
      key: 'address_map',
      component: 'address_map',
    };
  },
  questions: ({
    answers,
    inputContext: { marketDisplayName, answerPrefills },
  }): Array<FlowQuestion> => [
    {
      key: 'home.bedrooms',
      label: 'Bedrooms',
      type: 'dropdown_select',
      maxValue: 12,
      minValue: 1,
      helperText: 'Must have a door, closet, and window.',
    },
    {
      key: 'home.bathrooms.full',
      label: 'Full bathrooms',
      type: 'dropdown_select',
      maxValue: 10,
      minValue: 1,
      helperText: 'Must have a toilet and a shower/tub.',
    },
    {
      key: 'home.bathrooms.half',
      label: 'Partial bathrooms',
      helperText: 'Must have a toilet and sink.',
      type: 'dropdown_select',
      maxValue: 10,
      minValue: 0,
    },
    {
      key: 'home.above_grade_sq_ft',
      type: 'integer',
      label: 'Square footage (above ground)',
      helperText: 'Don’t include basements, non-permitted additions, or non-heated square footage.',
      maxValue: 10000,
      minValue: 100,
      suffix: ' ft²',
      placeholder: '0 ft²',
      showCommas: true,
      customValidatorErrorMessage: numberInputWithinValidRangeCustomErrorMessage(
        'home.above_grade_sq_ft',
        answers['home.above_grade_sq_ft'],
        100,
        10000,
        'Square footage',
        'sq. ft.',
      ),
    },
    {
      key: 'home.lot_sq_ft',
      type: 'integer',
      label: 'Lot square footage',
      helperText: 'This is the length x width of your lot (in feet).',
      minValue: 1000,
      maxValue: 108900,
      placeholder: '0 ft²',
      suffix: ' ft²',
      showCommas: true,
      hidden: marketDisplayName !== 'Austin' && !lotSizeNeedsRemediation(answers, answerPrefills),
      optional: true,
      customValidatorErrorMessage: numberInputWithinValidRangeCustomErrorMessage(
        'home.lot_sq_ft',
        answers['home.lot_sq_ft'],
        1000,
        108900,
        'Lot size',
        'sq. ft.',
      ),
    },
    {
      key: 'home.exterior_stories',
      type: 'dropdown_select',
      label: 'Floors (above ground)',
      helperText: "Don't include the basement.",
      maxValue: 3,
      minValue: 1,
      keepAnswerIfHidden: true,
    },
    {
      key: 'home.has_basement',
      label: 'Basement',
      helperText: 'Floors that are completely below ground.',
      type: 'dropdown_select',
      hidden: hideBasementFields(answers),
      optional: false,
      options: [
        {
          label: 'Yes',
          value: 'yes',
        },
        {
          label: 'No',
          value: 'no',
        },
      ],
    },
    {
      key: 'home.basement_finished_sq_ft',
      label: 'Finished basement sq. ft.',
      helperText: 'Heated, fully insulated, and waterproof.',
      type: 'integer',
      optional: !!answers['home.basement_unfinished_sq_ft'],
      minValue: 0,
      maxValue: 10000,
      placeholder: '0 ft²',
      suffix: ' ft²',
      showCommas: true,
      hidden: hideBasementFields(answers) || answers['home.has_basement'] == 'no',
      customValidatorErrorMessage:
        basementFinishedSqFtCustomValidator(answers) ||
        numberInputWithinValidRangeCustomErrorMessage(
          'home.basement_finished_sq_ft',
          answers['home.basement_finished_sq_ft'],
          0,
          10000,
          'Finished basement',
          'sq. ft.',
        ),
    },
    {
      key: 'home.basement_unfinished_sq_ft',
      label: 'Unfinished basement sq. ft.',
      helperText: 'Non-heated, concrete floors, exposed pipes or drywall.',
      type: 'integer',
      optional: !!answers['home.basement_finished_sq_ft'],
      minValue: 0,
      maxValue: 10000,
      placeholder: '0 ft²',
      suffix: ' ft²',
      showCommas: true,
      hidden: hideBasementFields(answers) || answers['home.has_basement'] == 'no',
      customValidatorErrorMessage:
        basementUnfinishedSqFtCustomValidator(answers) ||
        numberInputWithinValidRangeCustomErrorMessage(
          'home.basement_unfinished_sq_ft',
          answers['home.basement_unfinished_sq_ft'],
          0,
          10000,
          'Unfinished basement',
          'sq. ft.',
        ),
    },
    {
      key: 'home.year_built',
      label: 'Year built',
      type: 'dropdown_select',
      maxValue: new Date().getFullYear(),
      minValue: new Date().getFullYear() - 150,
    },
    {
      key: 'home.pool_type',
      label: 'Pool',
      type: 'dropdown_select',
      options: [
        {
          label: 'In-ground',
          value: 'in_ground',
        },
        {
          label: 'Above-ground',
          value: 'above_ground',
        },
        {
          label: 'Community pool',
          value: 'community',
        },
        {
          label: 'No pool',
          value: 'none',
        },
      ],
    },
    {
      key: 'home.covered_parking_type',
      label: 'Covered parking',
      type: 'dropdown_select',
      options: [
        {
          label: 'Garage',
          value: 'garage',
        },
        {
          label: 'Carport',
          value: 'carport',
        },
        {
          label: 'Both',
          value: 'both',
        },
        {
          label: 'None',
          value: 'none',
        },
      ],
    },
    {
      key: 'home.garage_spaces',
      label: 'Garage spaces',
      type: 'dropdown_select',
      maxValue: 10,
      minValue: 1,
      hidden:
        !answers['home.covered_parking_type'] ||
        answers['home.covered_parking_type'] === 'carport' ||
        answers['home.covered_parking_type'] === 'none',
    },
    {
      key: 'home.carport_spaces',
      label: 'Carport spaces',
      type: 'dropdown_select',
      maxValue: 10,
      minValue: 1,
      hidden:
        !answers['home.covered_parking_type'] ||
        answers['home.covered_parking_type'] === 'garage' ||
        answers['home.covered_parking_type'] === 'none',
    },
  ],
};

export default basicDataPage;
